import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useForm, Controller, type SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  FaCommentDots,
  FaFlag,
  FaPaperclip,
  FaPaperPlane,
  FaCloudUploadAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useSendHelpContact } from "../../api/mutations/useSendHelpContact";
import { User } from "../../types/Types";

type ReportFormValues = {
  message: string;
};

type ReportUserModalProps = {
  show: boolean;
  onHide: () => void;
  reportedUser: User | null;
};

function getInputClassName(hasError: boolean, base = "form-control rounded-3") {
  return `${base} ${hasError ? "is-invalid" : ""}`.trim();
}

function ReportUserModal({ show, onHide, reportedUser }: ReportUserModalProps) {
  const { t } = useTranslation();
  const [attachment, setAttachment] = useState<File | null>(null);
  const [submitError, setSubmitError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const schema = useMemo(
    () =>
      yup.object().shape({
        message: yup
          .string()
          .trim()
          .min(10, t("profile.report.validation.messageMin"))
          .required(t("profile.report.validation.messageRequired")),
      }),
    [t]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ReportFormValues>({
    resolver: yupResolver(schema),
    defaultValues: { message: "" },
  });

  useEffect(() => {
    if (!show) {
      reset({ message: "" });
      setAttachment(null);
      setSubmitError("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [show, reset]);

  const reportedName = useMemo(() => {
    if (!reportedUser) return "";
    return `${reportedUser.firstName || ""} ${reportedUser.lastName || ""}`.trim();
  }, [reportedUser]);

  const onSuccess = useCallback(() => {
    toast.success(t("profile.report.success"));
    onHide();
  }, [onHide, t]);

  const onError = useCallback(() => {
    setSubmitError(t("profile.report.submitError"));
  }, [t]);

  const { mutate: sendHelpContact, isPending } = useSendHelpContact({
    onSuccess,
    onError,
  });

  const onSubmit: SubmitHandler<ReportFormValues> = useCallback(
    (data) => {
      if (!reportedUser) return;
      setSubmitError("");

      const reportedUserId =
        reportedUser.objectId ||
        (reportedUser.id != null ? String(reportedUser.id) : undefined);

      sendHelpContact({
        category: t("profile.report.category"),
        categoryId: "report-user",
        message: data.message.trim(),
        reportedUserName: reportedName || undefined,
        reportedUserId,
        file: attachment,
      });
    },
    [attachment, reportedName, reportedUser, sendHelpContact, t]
  );

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setAttachment(e.target.files?.[0] ?? null);
  }, []);

  return (
    <Modal
      show={show}
      onHide={isPending ? undefined : onHide}
      centered
      backdrop="static"
      keyboard={!isPending}
      contentClassName="border-0 rounded-4 overflow-hidden shadow"
    >
      <Modal.Header
        closeButton={!isPending}
        closeVariant="white"
        className="bg-project-gradient text-white border-0 px-4 py-3"
      >
        <Modal.Title className="d-flex align-items-center gap-2 fs-5 fw-semibold">
          <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white bg-opacity-25 p-2">
            <FaFlag size={14} aria-hidden />
          </span>
          {t("profile.report.title")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="bg-body-tertiary p-3 p-md-4">
        {reportedName && (
          <p className="text-secondary small mb-3 mb-md-4">
            {t("profile.report.reporting", { name: reportedName })}
          </p>
        )}

        {submitError && (
          <div className="alert alert-danger rounded-4 py-2">{submitError}</div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className="bg-white border-0 shadow-sm rounded-4 p-3 p-md-4"
        >
          <div className="mb-4">
            <div className="d-flex align-items-center gap-2 mb-2">
              <FaCommentDots className="text-secondary" size={14} aria-hidden />
              <label
                htmlFor="report-user-message"
                className="form-label fw-semibold mb-0 text-dark"
              >
                {t("profile.report.reasonLabel")}
              </label>
            </div>
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <textarea
                  id="report-user-message"
                  className={getInputClassName(!!errors.message)}
                  rows={5}
                  placeholder={t("profile.report.reasonPlaceholder")}
                  disabled={isPending}
                  {...field}
                />
              )}
            />
            {errors.message && (
              <div className="invalid-feedback d-block">
                {errors.message.message}
              </div>
            )}
          </div>

          <div className="mb-4">
            <div className="d-flex align-items-center gap-2 mb-2">
              <FaPaperclip className="text-secondary" size={14} aria-hidden />
              <span className="form-label fw-semibold mb-0 text-dark">
                {t("profile.report.attachOptional")}
              </span>
            </div>
            <div className="border border-2 border-dashed rounded-4 p-3 text-center bg-body-tertiary">
              <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-body-secondary text-secondary p-2 mb-2">
                <FaCloudUploadAlt size={20} aria-hidden />
              </div>
              <p className="fw-semibold mb-1 small">
                {attachment
                  ? t("help.form.fileReady")
                  : t("help.form.uploadTitle")}
              </p>
              <p className="small text-muted mb-3 text-truncate px-2">
                {attachment ? attachment.name : t("help.form.attachHint")}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                className="d-none"
                accept="image/*,.pdf"
                onChange={handleFileChange}
                disabled={isPending}
              />
              <button
                type="button"
                className="btn btn-outline-primary rounded-pill px-3 btn-sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isPending}
              >
                {attachment
                  ? t("profile.report.changeFile")
                  : t("help.form.uploadFile")}
              </button>
            </div>
          </div>

          <div className="d-flex flex-column flex-sm-row justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary rounded-pill px-4"
              onClick={onHide}
              disabled={isPending}
            >
              {t("profile.report.cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary rounded-pill px-4 fw-semibold d-inline-flex align-items-center justify-content-center gap-2"
              disabled={isPending}
            >
              {isPending ? (
                <Spinner animation="border" size="sm" aria-hidden />
              ) : (
                <FaPaperPlane aria-hidden />
              )}
              {isPending
                ? t("profile.report.sending")
                : t("profile.report.submit")}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}

ReportUserModal.displayName = "ReportUserModal";

export default memo(ReportUserModal);
